// Real-time catch detector for boat acceleration a(t) (m/s², + = speeding up).
// Pipeline per sample: 10 s mean removal (drift) → ~5 Hz one-pole low-pass →
// catch search. Catch = lowest point of a between 0.5·T after the last catch
// and the next upward zero crossing. T = mean of last 3 stroke times, else
// 60/fallback rate, else 2.0 s. No catch within 1.6·T → lowest point in the
// window is used (logged as "missed catch"); if that window had no real dip
// the crew is treated as stopped and the search restarts.
const DRIFT_WINDOW_MS = 10000;
const LPF_HZ = 5;
const MIN_DIP = 0.15;           // m/s² — dips shallower than this are noise
const MIN_STROKE_MS = 60000 / 60;
const MAX_STROKE_MS = 60000 / 10;

export function createCatchDetector() {
  let win = [];
  let winSum = 0;
  let lpf = null;
  let lastT = 0;
  let prevA = 0;
  let lastCatchT = 0;
  let minA = Infinity;
  let minT = 0;
  let strokeTimes = [];
  let missedCount = 0;
  let growMs = 0; // after a missed catch the search window grows (rate dropped)

  const avg3 = () => strokeTimes.slice(-3).reduce((s, v) => s + v, 0) / 3;
  const expectedMs = (fallbackSpm) => {
    if (strokeTimes.length >= 3) return avg3();
    const fb = fallbackSpm > 0 ? 60000 / fallbackSpm : 0;
    if (growMs) return Math.max(growMs, fb);
    return fb || 2000;
  };

  const rateSpm = () => {
    if (strokeTimes.length < 2) return strokeTimes.length ? 60000 / strokeTimes[0] : 0;
    const last2 = strokeTimes.slice(-2);
    return 60000 / ((last2[0] + last2[1]) / 2);
  };

  function registerCatch(t, a, missed, T) {
    const strokeMs = lastCatchT ? t - lastCatchT : 0;
    const valid = !missed && strokeMs >= MIN_STROKE_MS && strokeMs <= MAX_STROKE_MS;
    if (missed) {
      // Interval is unreliable; drop history and widen the next window.
      strokeTimes = [];
      growMs = Math.min(MAX_STROKE_MS, 1.6 * T);
    } else if (valid) {
      strokeTimes.push(strokeMs);
      if (strokeTimes.length > 10) strokeTimes.shift();
      if (strokeTimes.length >= 3) growMs = 0;
    }
    lastCatchT = t;
    minA = Infinity;
    minT = 0;
    if (missed) missedCount += 1;
    return { t, a, strokeMs: valid ? strokeMs : 0, missed, rateSpm: rateSpm(), strokeTimes: [...strokeTimes] };
  }

  function push(t, raw, fallbackSpm = 0) {
    // Drift removal (mean over last 10 s)
    win.push([t, raw]);
    winSum += raw;
    while (win.length && t - win[0][0] > DRIFT_WINDOW_MS) winSum -= win.shift()[1];
    const centred = raw - winSum / win.length;

    // ~5 Hz low-pass (one-pole, dt-aware)
    const dt = lastT ? Math.max(1, t - lastT) / 1000 : 0.02;
    lastT = t;
    const rc = 1 / (2 * Math.PI * LPF_HZ);
    const alpha = dt / (rc + dt);
    lpf = lpf == null ? centred : lpf + alpha * (centred - lpf);
    const a = lpf;

    // Independent rate (autocorrelation) disagrees by >35 % → rate changed sharply
    // (e.g. 18 → 36 spm would otherwise lock at half rate). Re-seed from it.
    if (fallbackSpm > 0 && strokeTimes.length >= 3 && Math.abs(60000 / fallbackSpm - avg3()) / avg3() > 0.35) {
      strokeTimes = [];
    }
    const T = expectedMs(fallbackSpm);
    if (!lastCatchT) lastCatchT = t;
    const since = t - lastCatchT;
    let evt = null;

    if (since >= 0.5 * T) {
      if (a < minA) { minA = a; minT = t; }
      if (prevA < 0 && a >= 0 && minT && minA <= -MIN_DIP) {
        evt = registerCatch(minT, minA, false, T);
      } else if (since > 1.6 * T) {
        if (minT && minA <= -MIN_DIP) {
          evt = registerCatch(minT, minA, true, T);
        } else {
          // Stopped / paddling too lightly: restart the search, drop history.
          lastCatchT = t;
          minA = Infinity;
          minT = 0;
          strokeTimes = [];
        }
      }
    }
    prevA = a;
    return { a, catchEvt: evt, expectedMs: expectedMs(fallbackSpm) };
  }

  return {
    push,
    expectedMs,
    rateSpm,
    get lastCatchT() { return lastCatchT; },
    get missedCount() { return missedCount; },
    reset() {
      win = []; winSum = 0; lpf = null; lastT = 0; prevA = 0;
      lastCatchT = 0; minA = Infinity; minT = 0; strokeTimes = []; missedCount = 0; growMs = 0;
    },
  };
}
